import { z } from "zod";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";

export const openingBalanceLineSchema = z.object({
    description: z.string().nullable(),
    balance: z.number(),
});

export const mainOpeningBalanceSchema = z.object({
    id: z.string().uuid(),
    date: z.string().date(),
    accountId: z.string().uuid(),
    clearingAccountId: z.string().uuid(),
    clearingBalanceDescription: z.string().nullable(),
    lines: z.array(openingBalanceLineSchema),
});

export const openingBalanceAdjustmentSchema = z.object({
    balance: z.number(),
    date: z.string().date(),
    clearingAccountId: z.string().uuid().optional(),
    description: z.string().nullable().optional(),
});

export const getOpeningBalanceResponseSchema = z.object({
    openingBalance: mainOpeningBalanceSchema.nullable(),
    adjustment: openingBalanceAdjustmentSchema.nullable(),
    activityExcludingMainOpeningBalance: z.number(),
});

export const openingBalancePatchSchema = z.object({
    date: z.string().date(),
    clearingAccountId: z.string().uuid(),
    clearingBalanceDescription: z.string().nullable().optional(),
    lines: z.array(openingBalanceLineSchema),
});

export const openingBalanceAdjustmentPatchSchema = z.object({
    balance: z.number(),
    date: z.string().date(),
    clearingAccountId: z.string().uuid().optional(),
    description: z.string().nullable().optional(),
});

export const upsertOpeningBalanceBodySchema = z.object({
    openingBalance: openingBalancePatchSchema.nullable().optional(),
    adjustment: openingBalanceAdjustmentPatchSchema.nullable().optional(),
});

export const openingBalanceLedgerBasisQuerySchema = z.object({
    ledgerBasis: z.nativeEnum(AccountingLedgerBasis).optional(),
});

export const listOpeningBalanceSchema = z.object({
    accountId: z.string().uuid(),
    openingBalance: z.number(),
});

export const getOpeningBalancesResponseSchema = z.array(
    listOpeningBalanceSchema,
);

export const setOpeningBalancesBulkBodySchema = z.object({
    openingBalances: z.array(
        z.object({
            accountId: z.string().uuid(),
            balance: z.number().refine((v) => v !== 0, {
                message:
                    "Opening balance must be nonzero. Remove zero-balance rows — accounts with no balance don't need an opening-balance row.",
            }),
            description: z.string().optional(),
        }),
    ),
    financialDocumentId: z.number().optional(),
});

export const unreconciledKickBalanceSchema = z.object({
    financialAccountId: z.number(),
    kickOpeningBalance: z.number().nullable(),
});

export const getUnreconciledKickBalancesResponseSchema = z.array(
    unreconciledKickBalanceSchema,
);

export type OpeningBalanceLine = z.infer<typeof openingBalanceLineSchema>;
export type MainOpeningBalance = z.infer<typeof mainOpeningBalanceSchema>;
export type OpeningBalanceAdjustment = z.infer<
    typeof openingBalanceAdjustmentSchema
>;
export type GetOpeningBalanceResponse = z.infer<
    typeof getOpeningBalanceResponseSchema
>;
export type OpeningBalancePatch = z.infer<typeof openingBalancePatchSchema>;
export type OpeningBalanceAdjustmentPatch = z.infer<
    typeof openingBalanceAdjustmentPatchSchema
>;
export type UpsertOpeningBalanceBody = z.infer<
    typeof upsertOpeningBalanceBodySchema
>;
export type OpeningBalance = z.infer<typeof listOpeningBalanceSchema>;
export type SetOpeningBalancesBulkBody = z.infer<
    typeof setOpeningBalancesBulkBodySchema
>;
